import React, { useState, useRef, useEffect } from 'react';
import { Star, Quote, ArrowLeft, ArrowRight } from 'lucide-react';
import { mockData } from '../data/mock';

const TestimonialsSection = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % mockData.testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % mockData.testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => prev === 0 ? mockData.testimonials.length - 1 : prev - 1);
  };

  return (
    <section id="testimonials" ref={sectionRef} className="py-20 bg-gradient-to-br from-blue-50/30 to-indigo-50/30 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 -left-32 w-64 h-64 bg-gradient-to-r from-blue-200/30 to-indigo-200/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 -right-32 w-64 h-64 bg-gradient-to-r from-indigo-200/30 to-blue-200/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
        
        {/* Floating quote symbols */}
        <div className="absolute top-20 left-20 text-6xl text-blue-200/30 font-serif">"</div>
        <div className="absolute bottom-20 right-20 text-6xl text-indigo-200/30 font-serif transform rotate-180">"</div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}>
          <div className="inline-flex items-center space-x-2 text-sm font-medium text-blue-600 bg-blue-100/80 px-4 py-2 rounded-full mb-4">
            <Star size={16} />
            <span>Success Stories</span>
          </div>
          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Client
            </span>
            <br />
            Success Stories
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Hear from government agencies, school boards, and organizations who have transformed their digital presence with our solutions.
          </p>
        </div>

        {/* Main Testimonial */}
        <div className={`relative mb-16 transition-all duration-1000 delay-300 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}>
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-2xl relative overflow-hidden">
              {/* Background pattern */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 opacity-50"></div>
              
              <div className="relative z-10">
                {/* Quote icon */}
                <div className="flex justify-center mb-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                    <Quote className="h-8 w-8 text-white" />
                  </div>
                </div>

                {/* Testimonial content */}
                <div className="text-center space-y-8">
                  <blockquote className="text-2xl lg:text-3xl text-gray-700 leading-relaxed font-medium">
                    "{mockData.testimonials[currentTestimonial].content}"
                  </blockquote>

                  {/* Rating */}
                  <div className="flex justify-center space-x-1">
                    {Array.from({ length: mockData.testimonials[currentTestimonial].rating }).map((_, index) => (
                      <Star key={index} className="h-6 w-6 text-yellow-400 fill-current" />
                    ))}
                  </div>

                  {/* Client info */}
                  <div className="flex items-center justify-center space-x-4 pt-8 border-t border-gray-200">
                    <img
                      src={mockData.testimonials[currentTestimonial].image}
                      alt={mockData.testimonials[currentTestimonial].name}
                      className="w-16 h-16 rounded-full object-cover ring-4 ring-blue-200"
                    />
                    <div className="text-left">
                      <div className="text-xl font-bold text-gray-900">
                        {mockData.testimonials[currentTestimonial].name}
                      </div>
                      <div className="text-blue-600 font-semibold">
                        {mockData.testimonials[currentTestimonial].position}
                      </div>
                      <div className="text-gray-600">
                        {mockData.testimonials[currentTestimonial].organization}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-gradient-to-br from-blue-400/20 to-indigo-400/20 rounded-full"></div>
              <div className="absolute -bottom-4 -right-4 w-16 h-16 bg-gradient-to-br from-indigo-400/20 to-blue-400/20 rounded-full"></div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-center space-x-4 mt-8">
              <button
                onClick={prevTestimonial}
                className="p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 group border border-gray-200 hover:border-blue-300"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600 group-hover:text-blue-600" />
              </button>

              {/* Dots indicator */}
              <div className="flex space-x-2">
                {mockData.testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      currentTestimonial === index
                        ? 'bg-blue-500 scale-125'
                        : 'bg-gray-300 hover:bg-blue-300'
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={nextTestimonial}
                className="p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 group border border-gray-200 hover:border-blue-300"
              >
                <ArrowRight className="h-5 w-5 text-gray-600 group-hover:text-blue-600" />
              </button>
            </div>
          </div>
        </div>

        {/* All testimonials preview */}
        <div className={`grid md:grid-cols-3 gap-6 transition-all duration-1000 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}>
          {mockData.testimonials.map((testimonial, index) => (
            <div
              key={index}
              className={`bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-105 border-2 ${
                currentTestimonial === index ? 'border-blue-500 ring-4 ring-blue-200' : 'border-gray-200 hover:border-blue-300'
              }`}
              onClick={() => setCurrentTestimonial(index)}
            >
              <div className="flex items-center space-x-4 mb-4">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <div className="font-bold text-gray-900 text-sm">{testimonial.name}</div>
                  <div className="text-blue-600 text-xs font-semibold">{testimonial.position}</div>
                  <div className="text-gray-500 text-xs">{testimonial.organization}</div>
                </div>
              </div>
              
              <p className="text-gray-600 text-sm leading-relaxed">
                "{testimonial.content.substring(0, 120)}..."
              </p>
              
              <div className="flex space-x-1 mt-3">
                {Array.from({ length: testimonial.rating }).map((_, starIndex) => (
                  <Star key={starIndex} className="h-4 w-4 text-yellow-400 fill-current" />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Stats */}
        <div className={`mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 transition-all duration-1000 delay-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}>
          {mockData.hero.stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-4xl lg:text-5xl font-bold text-blue-600 mb-2">
                {stat.number}
              </div>
              <div className="text-gray-600 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;